import { message } from "antd";
import axios from "axios";
import { LC_KEYS } from "./constants";

export type ImpersonationSessionInfo = {
  /** Email of the user being impersonated */
  email: string;
  /** Email of the admin running the session */
  admin: string;
  /** ISO timestamp when the impersonation token expires */
  expires: string;
  /** ISO timestamp when the session was opened in this browser */
  started_at: string;
};

// GOD mode (admin login-as-user) sessions are TAB-SCOPED: the token and its
// metadata live in sessionStorage, never localStorage. This keeps an
// impersonation session isolated to the tab the admin opened it in — the
// admin's own login (in localStorage) and any other tabs are untouched, and
// no other tab silently starts authenticating as the impersonated user.

/**
 * The auth token for API requests: the tab-scoped impersonation token when
 * present, otherwise the normal (localStorage) session token. All request
 * paths must read the token through this accessor so GOD mode stays isolated
 * to its own tab.
 */
export const getStoredToken = (): string | null => sessionStorage.getItem("token") ?? localStorage.getItem("token");

/**
 * Returns the GOD mode session metadata for THIS tab, or null in a normal tab.
 */
export const getImpersonationSession = (): ImpersonationSessionInfo | null => {
  try {
    const raw = sessionStorage.getItem(LC_KEYS.IMPERSONATION);
    return raw ? (JSON.parse(raw) as ImpersonationSessionInfo) : null;
  } catch {
    return null;
  }
};

/** Prefix on every impersonation token (mirrors the backend KEY_PREFIX). */
const IMPERSONATION_TOKEN_PREFIX = "imp_";

/**
 * True when this tab is a GOD mode session. Keyed off the authoritative signals
 * — the early `window.__NUDGE_GOD_MODE__` flag (set by the first inline script,
 * so it is true even on the /impersonate handoff page before a token is stored)
 * and the imp_ token in sessionStorage — so no guard (analytics, timezone,
 * Intercom, logout) ever runs as the real user while impersonation is active.
 */
export const isGodMode = (): boolean => {
  if (typeof window !== "undefined" && (window as { __NUDGE_GOD_MODE__?: boolean }).__NUDGE_GOD_MODE__) {
    return true;
  }
  const token = sessionStorage.getItem("token");
  return (token?.startsWith(IMPERSONATION_TOKEN_PREFIX) ?? false) || getImpersonationSession() !== null;
};

/**
 * Persist a validated GOD mode session into this tab's sessionStorage. Written
 * transactionally: if the metadata write fails, the token is rolled back too so
 * a half-written handoff can never leave a bare token that silently activates
 * impersonation without a banner.
 */
export const setImpersonationSession = (token: string, info: ImpersonationSessionInfo): void => {
  try {
    sessionStorage.setItem("token", token);
    sessionStorage.setItem(LC_KEYS.IMPERSONATION, JSON.stringify(info));
  } catch (e) {
    clearImpersonationTab();
    throw e;
  }
};

/** Clear only this tab's GOD mode session; never touches the localStorage login. */
export const clearImpersonationTab = (): void => {
  sessionStorage.removeItem("token");
  sessionStorage.removeItem(LC_KEYS.IMPERSONATION);
};

/**
 * Ends a GOD mode session: revokes the impersonation token server-side (the
 * dedicated endpoint only ever touches the impersonation token — NEVER
 * /rest-auth/logout/, which would delete the user's real token and log them
 * out of all their devices), then clears this tab's session and returns to
 * /login. Uses a bare axios call (with an explicitly scrubbed error log) so a
 * still-valid credential can never leak into telemetry.
 *
 * On a transient failure (network error / 5xx) the server session is still
 * live, so we KEEP the local session and surface the error for a retry rather
 * than silently stranding a usable credential until its TTL. A 401/403 means
 * the session is already gone server-side, so clearing is safe.
 */
export const closeImpersonationSession = async (): Promise<void> => {
  const token = getStoredToken();
  try {
    await axios.post(
      `${process.env.REACT_APP_SERVER_URL}/users/impersonation/close/`,
      {},
      { headers: token ? { Authorization: `Token ${token}` } : {}, withCredentials: true },
    );
  } catch (error) {
    // Never log the raw AxiosError — its config.headers carries the bearer token.
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    if (status !== 401 && status !== 403) {
      console.error(`Failed to revoke impersonation session (status: ${status ?? "network error"})`);
      message.error("Couldn't end the session — please try again.");
      return; // keep the session so the admin can retry the revocation
    }
  }
  clearImpersonationTab();
  window.location.href = "/login";
};
